import React, { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { Divider, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import CapturePhoto from 'modules/views/shared/CapturePhoto';
import AddImages from 'modules/views/shared/AddImages';
import SkippableFieldsModal from 'modules/views/shared/SkippableFieldsModal';
import SkippableFieldIndicator from 'modules/views/shared/SkippableFieldIndicator';
import Image from 'modules/views/shared/Image';
import { FieldWrapper } from './components/fieldWrapper';
import { ReactComponent as CameraIcon } from 'assets/icons/CameraIcon.svg';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import PiiPinVerificationModals from 'modules/views/shared/PiiPinVerificationModals';

import personalInformationSchema, { IPersonalInformationSchema } from './schema';
import useForm from 'hooks/useForm';
import { createGuid, prettifyErrorResponse, formatBackendValidationErrors, getBackendErrorsFromResponse } from 'utils';
import { extractCustomFieldCodes } from 'utils/customFieldsUtils';
import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import useUploadProfilePicture from 'hooks/useUploadProfilePicture';
import {
  uploadGovernmentDocumentFiles,
  deleteGovernmentDocumentFile,
  addGovernmentDocumentFile,
  updateWorker,
} from 'modules/state-mgmt/onboarding';
import { setOnboarding } from 'modules/state-mgmt/onboarding';
import SignatureSection from 'modules/views/shared/SignatureSection/SignatureSection';

import { IFile } from 'models/general';
import { actionMapForOtherLoaders } from 'constants/actions';
import { useStyles } from './styles';
import { GOV_ID_TYPE_OTHER_OPTION_ID } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import { getSkippedFields, shouldEnforceFieldAsMandatory } from 'utils/skippableFieldsUtils';
import { usePersonalInformationFields, PERSONAL_INFO_FIELD_CONFIG } from 'hooks/usePersonalInformationFields';
import { WorkerProfileField } from 'types/profileCustomFields';
import { getImageFormat } from 'utils/files';

// Synced into the form by effects (from Redux / sibling upload APIs) rather than edited by
// the user, and not part of the updateWorker payload — so they must never mark the form dirty.
const NON_DIRTY_FIELDS: Array<keyof IPersonalInformationSchema> = ['governmentDocumentFiles', 'pictureUrl'];

const PersonalInformation = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { goToNextStep, handlePreviousStep, canGoToPreviousStep, getNextStepName } = useWizardStepNavigation();

  const {
    onboarding,
    profileFieldConstraintsMap,
    workerId,
    languageCode,
    governmentDocumentFiles,
    uploadGovernmentDocumentFilesLoading,
    updateWorkerLoading,
    hasReachedConsentForm,
    isArchivedProject,
    isOffline,
    ethnicities,
    languages,
    iDTypes,
    states,
    isWorker,
  } = useAppSelector(state => ({
    onboarding: state.onboarding,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    workerId: state.onboarding?.id,
    languageCode: state.app?.languageCode,
    governmentDocumentFiles: state.onboarding.governmentDocumentFiles,
    uploadGovernmentDocumentFilesLoading:
      state.loader.otherLoaders[actionMapForOtherLoaders.uploadGovernmentDocumentFiles],
    updateWorkerLoading: state.loader.otherLoaders[actionMapForOtherLoaders.updateWorker],
    hasReachedConsentForm: state.onboarding.temp?.hasReachedConsentForm || false,
    isArchivedProject: state.cache.project.status === 3,
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    ethnicities: state.cache.ethnicities[state.app?.languageCode] || [],
    languages: state.cache.languages[state.app?.languageCode] || [],
    iDTypes: state.cache.iDTypes[state.app?.languageCode] || [],
    states: state.cache.states[state.app?.languageCode] || [],
    isWorker: !state.user?.userId || state.user?.isWorker,
  }));
  const isLoading = uploadGovernmentDocumentFilesLoading?.isLoading || updateWorkerLoading?.isLoading;
  const hasPendingUploadErrorForPage =
    onboarding?.uploadingDetails?.errors?.pageUrl === ROUTES.PERSONAL_INFORMATION.path;

  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [showPiiPinVerificationModal, setShowPiiPinVerificationModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  // Modal state for showing skippable fields warning
  const [showSkippableFieldsModal, setShowSkippableFieldsModal] = useState(false);

  const { isLoading: isProfilePictureUploading, uploadProfilePicture } = useUploadProfilePicture({
    successCallback: () => {
      setShowCaptureModal(false);
    },
    failureCallback: () => {
      setShowCaptureModal(false);
    },
  });

  const maxDate = moment();
  maxDate.subtract(18, 'years');

  const initialValues: IPersonalInformationSchema = {
    firstName: onboarding?.firstName,
    middleName: onboarding?.middleName,
    lastName: onboarding?.lastName,
    dateOfBirth: onboarding?.dateOfBirth || null,
    gender: onboarding?.gender,
    ethnicityId: onboarding?.ethnicity?.id,
    primaryLanguageId: onboarding?.primaryLanguage?.id,
    isVeteran: onboarding?.isVeteran,
    allergies: onboarding?.allergies || '',
    socialSecurityNumber: onboarding?.socialSecurityNumber,
    identificationTypeId: onboarding?.identificationType?.id,
    identificationGeographicLocationId: onboarding?.identificationGeographicLocation?.id,
    identificationNumber: onboarding?.identificationNumber,
    governmentDocumentFiles: governmentDocumentFiles || [],
    otherIdentificationType: onboarding?.otherIdentificationType || '',
    pictureUrl: onboarding?.pictureUrl || null,
  };

  const validationSchema = useMemo(
    () => personalInformationSchema(t, profileFieldConstraintsMap, hasReachedConsentForm),
    [t, profileFieldConstraintsMap, hasReachedConsentForm],
  );

  const handleFormSubmit = async (values: IPersonalInformationSchema) => {
    // Skip the worker update entirely when the user did not edit anything on this page.
    // Forced through when either:
    // - a previous background upload failed for this page (the payload still needs a retry), or
    // - offline gov ID images were added/removed (only this submit flags them for upload).
    const hasGovernmentDocumentChangesToPersist =
      governmentDocumentFiles?.some(file => file.tempId || file.isDeleted) || false;
    const needsWorkerUpdate =
      getIsDirty() ||
      hasPendingUploadErrorForPage ||
      (isOffline && !isWorker && hasGovernmentDocumentChangesToPersist);

    if (!needsWorkerUpdate) {
      goToNextStep();
      return;
    }

    try {
      const apiData = {
        // Personal Information
        firstName: values.firstName?.trim(),
        middleName: values.middleName?.trim(),
        lastName: values.lastName?.trim(),
        dateOfBirth: values.dateOfBirth
          ? typeof values.dateOfBirth === 'string'
            ? values.dateOfBirth
            : (values.dateOfBirth as any).format?.('YYYY-MM-DD')
          : null,
        ethnicityId: values.ethnicityId || null,
        primaryLanguageId: values.primaryLanguageId || null,
        isVeteran: values.isVeteran,
        allergies: values.allergies?.trim() || null,
        socialSecurityNumber: values.socialSecurityNumber || null,
        gender: values.gender,
        identificationTypeId: values?.identificationTypeId,
        ...(values?.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID
          ? { otherIdentificationType: values?.otherIdentificationType?.trim() }
          : { identificationGeographicLocationId: values?.identificationGeographicLocationId }),
        identificationNumber: values?.identificationNumber,

        // Other required parameters
      };

      // Extract custom field codes for Personal Information screen
      const workerCustomFieldCodesToValidate = extractCustomFieldCodes(PERSONAL_INFO_FIELD_CONFIG);

      if (isOffline && !isWorker) {
        // Explicit Offline Flow: Reconstruct Objects
        const reconstructObject = (id: string | null | undefined, list: any[]) => {
          if (!id) return null;
          const item = list?.find(x => x.id === id);
          return item ? { id: item.id, name: item.name } : null;
        };

        const offlinePayload = {
          ...apiData,
          ethnicity: reconstructObject(apiData.ethnicityId, ethnicities),
          primaryLanguage: reconstructObject(apiData.primaryLanguageId, languages),
          identificationType: reconstructObject(apiData.identificationTypeId, iDTypes),
          identificationGeographicLocation: reconstructObject(
            (apiData as any).identificationGeographicLocationId,
            states,
          ),
        };

        // Remove the ID-only fields from the offline payload (optional)
        delete offlinePayload.ethnicityId;
        delete offlinePayload.primaryLanguageId;
        delete offlinePayload.identificationTypeId;
        delete offlinePayload.identificationTypeId;

        const updatedState = {
          ...offlinePayload,
          uploadingDetails: {
            ...onboarding.uploadingDetails,
            isOffline: true,
            pendingUploads: {
              ...onboarding.uploadingDetails?.pendingUploads,
              personalInformation: true,
              govDocuments:
                governmentDocumentFiles?.filter(d => d.tempId).length > 0 ||
                governmentDocumentFiles?.filter(d => d.isDeleted).length > 0,
            },
            errors:
              onboarding.uploadingDetails?.errors?.pageUrl === ROUTES.PERSONAL_INFORMATION.path
                ? { pageUrl: '', errorObject: null }
                : onboarding.uploadingDetails?.errors,
          },
        };

        await dispatch(setOnboarding(updatedState));

        goToNextStep();
        return;
      }

      const res = await dispatch(
        updateWorker({
          workerId,
          data: apiData,
          languageCode,
          workerCustomFieldCodesToValidate,
        }),
      );

      if (res.type.includes('fulfilled')) {
        goToNextStep();
      } else {
        // Handle backend validation errors - show all errors in snackbar
        const errorMessage = formatBackendValidationErrors(res.payload);
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
      }
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  const onUploadProfilePicture = (imageSrc: string | string[]) => {
    const imageSource = Array.isArray(imageSrc) ? imageSrc[0] : imageSrc;
    uploadProfilePicture({ imageSrc: imageSource });
  };

  const onAddGovernmentDocumentFile = useCallback(
    (imageSrc: string | string[]) => {
      const isArray = Array.isArray(imageSrc);
      if (isArray) {
        (imageSrc as string[]).forEach(imgSrc => {
          const tempId = createGuid();
          const imageFormat = getImageFormat(imgSrc);
          dispatch(
            addGovernmentDocumentFile({
              tempId: tempId,
              image: imgSrc,
              fileName: `${tempId}.${imageFormat}`,
            }),
          );
        });
        return;
      } else {
        const tempId = createGuid();
        const imageFormat = getImageFormat(imageSrc);
        dispatch(
          addGovernmentDocumentFile({
            tempId: tempId,
            image: imageSrc,
            fileName: `${tempId}.${imageFormat}`,
          }),
        );
      }
    },
    [dispatch],
  );

  const onUploadGovernmentDocumentFiles = useCallback(
    (formValues: IPersonalInformationSchema) => {
      dispatch(uploadGovernmentDocumentFiles())
        .then(res => {
          if (res.type.includes('fulfilled')) {
            handleFormSubmit(formValues);
          } else {
            enqueueSnackbar(prettifyErrorResponse(res?.payload), {
              variant: 'error',
            });
          }
        })
        .catch(err => {
          enqueueSnackbar(prettifyErrorResponse(err), {
            variant: 'error',
          });
        });
    },
    [dispatch, handleFormSubmit],
  );

  const onDeleteGovernmentDocumentFile = useCallback(
    (file: IFile) => {
      setDeleteId(file.id || file.tempId);

      if (isOffline && !isWorker) {
        const updatedFiles = file.id
          ? governmentDocumentFiles?.map(f => (f.id === file.id ? { ...f, isDeleted: true } : f)) || []
          : governmentDocumentFiles?.filter(f => {
              if (file.tempId) return f.tempId !== file.tempId;
              if (file.id) return f.id !== file.id;
              return true;
            }) || [];
        dispatch(
          setOnboarding({
            governmentDocumentFiles: updatedFiles,
            uploadingDetails: {
              ...onboarding.uploadingDetails,
              isOffline: true,
              pendingUploads: { ...onboarding.uploadingDetails?.pendingUploads, personalInformation: true },
            },
          }),
        );
        setDeleteId('');
        return;
      }

      dispatch(
        deleteGovernmentDocumentFile({
          fileId: file.id,
          tempId: file.tempId,
          workerId: workerId,
          languageCode: languageCode,
        }),
      )
        .then(res => {
          if (res.type.includes('fulfilled')) {
            // Success
          } else {
            enqueueSnackbar(prettifyErrorResponse(res?.payload), {
              variant: 'error',
            });
          }
        })
        .catch(err => {
          enqueueSnackbar(prettifyErrorResponse(err), {
            variant: 'error',
          });
        })
        .finally(() => {
          setDeleteId('');
        });
    },
    [dispatch, workerId, languageCode, governmentDocumentFiles, isOffline, isWorker, onboarding],
  );

  const onSubmit = async (formValues: IPersonalInformationSchema) => {
    if (isArchivedProject) {
      goToNextStep();
      return;
    }

    // Check for skippable fields:
    // - Before consent form: All skippable fields trigger warning
    // - After consent form: Only Badge Printing/Activation fields trigger warning
    const dynamicFormFieldMap = getDynamicFormFieldMap(formValues);
    const skippedFields = getSkippedFields(
      formValues,
      profileFieldConstraintsMap,
      hasReachedConsentForm,
      fieldDisplayNames,
      dynamicFormFieldMap,
    );

    if (skippedFields.length > 0) {
      setShowSkippableFieldsModal(true);
      return;
    }

    // Proceed with normal form submission
    if (governmentDocumentFiles?.filter(d => d.tempId).length > 0 && !(isOffline && !isWorker)) {
      onUploadGovernmentDocumentFiles(formValues);
    } else {
      handleFormSubmit(formValues);
    }
  };

  const { values, errors, handleSubmit, setValue, setErrors, getIsDirty } = useForm({
    initialValues,
    validationSchema,
    ignoreValidations: isArchivedProject,
    nonDirtyKeys: NON_DIRTY_FIELDS,
    onSubmit,
  });

  // Generate fields with actual form values
  const {
    personalInformationFields,
    genderSectionFields,
    govtIdFields,
    govtIdImageField,
    fieldDisplayNames,
    isGovtFieldsSectionVisible,
    getDynamicFormFieldMap,
    profilePhotoField,
  } = usePersonalInformationFields(profileFieldConstraintsMap, hasReachedConsentForm, values);

  // Handle when user chooses to continue with skipped fields
  const handleContinueWithSkippedFields = useCallback(async () => {
    setShowSkippableFieldsModal(false);

    // Proceed with form submission
    if (governmentDocumentFiles?.filter(d => d.tempId).length > 0 && !(isOffline && !isWorker)) {
      onUploadGovernmentDocumentFiles(values);
    } else {
      handleFormSubmit(values);
    }
  }, [values, governmentDocumentFiles, onUploadGovernmentDocumentFiles, handleFormSubmit, isOffline, isWorker]);

  useEffect(() => {
    setValue('governmentDocumentFiles', governmentDocumentFiles || []);
  }, [governmentDocumentFiles, setValue]);

  useEffect(() => {
    setValue('pictureUrl', onboarding.pictureUrl || null);
  }, [onboarding.pictureUrl, setValue]);

  // On mount, populate form errors from background upload errors (if any)
  useEffect(() => {
    const uploadErrors = onboarding?.uploadingDetails?.errors;
    if (uploadErrors?.pageUrl === ROUTES.PERSONAL_INFORMATION.path && uploadErrors?.errorObject) {
      const personalInfoFields = [
        'firstName',
        'middleName',
        'lastName',
        'dateOfBirth',
        'gender',
        'ethnicityId',
        'primaryLanguageId',
        'isVeteran',
        'allergies',
        'socialSecurityNumber',
        'identificationTypeId',
        'identificationGeographicLocationId',
        'identificationNumber',
        'governmentDocumentFiles',
        'pictureUrl',
      ];
      const errorFieldMapper = {
        ethnicityId: 'ethnicity',
        primaryLanguageId: 'primaryLanguage',
        isVeteran: 'veteranStatus',
        socialSecurityNumber: 'lastSSN',
        identificationTypeId: 'governmentIDType',
        identificationGeographicLocationId: 'governmentIDIssuedBy',
        identificationNumber: 'governmentIDNumber',
        governmentDocumentFiles: 'governmentIDImage',
        pictureUrl: 'profilePhoto',
      };
      const backendErrors = getBackendErrorsFromResponse(
        uploadErrors.errorObject,
        personalInfoFields,
        errorFieldMapper,
      );
      if (backendErrors) {
        setErrors(backendErrors);
      }
    }
  }, []);

  // Show profile picture error: by default after consent form is reached, or after submit attempt
  const profilePictureErrorMessage = useMemo(() => {
    if (errors.pictureUrl?.message) return errors.pictureUrl.message;
    if (
      hasReachedConsentForm &&
      shouldEnforceFieldAsMandatory(
        WorkerProfileField.workerProfilePhoto,
        profileFieldConstraintsMap,
        hasReachedConsentForm,
      ) &&
      !onboarding.pictureUrl
    ) {
      return t('personalInformation.errors.profilePictureRequired');
    }
    return null;
  }, [errors.pictureUrl, hasReachedConsentForm, profileFieldConstraintsMap, onboarding.pictureUrl, t]);

  const content = (
    <div>
      <ContentHeader title={t('personalInformation.title')} subtitle={t('personalInformation.subtitle')} />
      <div className={classes.profilePicMainContainer}>
        <div
          className={`${classes.profilePicContainer} ${isArchivedProject ? classes.cursorDisabled : ''}`}
          onClick={() => !isArchivedProject && setShowCaptureModal(true)}
          role="button"
          aria-label="Upload profile picture"
          data-testid="profile-picture-button"
        >
          {onboarding.pictureUrl ? (
            <Image src={onboarding.pictureUrl} alt="Profile" className={classes.profileImage} />
          ) : (
            <p className={classes.profilePicText}>
              {values.firstName ? values.firstName.charAt(0).toUpperCase() : 'D'}
            </p>
          )}
          <div className={classes.cameraIconContainer}>
            <CameraIcon />
          </div>
        </div>
        {profilePhotoField.isVisible && (
          <>
            <SkippableFieldIndicator show={profilePhotoField.showSkippableIndicator || false} />
            {profilePictureErrorMessage && <p className={classes.profilePicErrorText}>{profilePictureErrorMessage}</p>}
          </>
        )}
      </div>
      <p className={classes.sectionTile}>{t('personalInformation.personalInformation.title')}</p>
      <Grid container spacing={3}>
        {personalInformationFields.map((field, index) => {
          if (!field.isVisible) return null;
          return (
            <Grid item xs={12} md={6} lg={4} key={field.id}>
              <FieldWrapper
                field={field}
                index={index}
                value={values[field.id]}
                setValue={value => {
                  setValue(field.id as keyof IPersonalInformationSchema, value);
                }}
                error={errors[field.id]?.message}
                options={field?.options}
                disabled={isArchivedProject}
                onPiiFieldClick={() => setShowPiiPinVerificationModal(true)}
              />
            </Grid>
          );
        })}
      </Grid>
      <Grid container spacing={3} className={classes.genderContainer}>
        {genderSectionFields.map((field, index) => {
          if (!field.isVisible) return null;
          return (
            <Grid item xs={12} md={12} lg={12} key={field.id}>
              <FieldWrapper
                field={field}
                index={index}
                value={values[field.id]}
                setValue={value => {
                  setValue(field.id as keyof IPersonalInformationSchema, value);
                }}
                error={errors[field.id]?.message}
                options={field?.options}
                disabled={isArchivedProject}
                styleClass={classes.genderParent}
                onPiiFieldClick={() => setShowPiiPinVerificationModal(true)}
              />
            </Grid>
          );
        })}
      </Grid>
      {isGovtFieldsSectionVisible && (
        <>
          <Divider className={classes.divider} />
          <p className={classes.sectionTile}>{t('personalInformation.govtId.title')}</p>
          <Grid container spacing={3}>
            {govtIdFields.map((field, index) => {
              if (!field.isVisible) return null;
              return (
                <Grid item xs={12} md={6} lg={4} key={field.id}>
                  <FieldWrapper
                    field={field}
                    index={index}
                    value={values[field.id]}
                    setValue={value => {
                      setValue(field.id as keyof IPersonalInformationSchema, value);
                      if (field.id === 'identificationTypeId') {
                        setValue('identificationGeographicLocationId', null);
                        setValue('otherIdentificationType', '');
                      }
                    }}
                    error={errors[field.id]?.message}
                    options={field?.options}
                    disabled={isArchivedProject}
                  />
                </Grid>
              );
            })}
          </Grid>
          {govtIdImageField.isVisible && (
            <>
              <AddImages
                images={governmentDocumentFiles?.filter(file => !file.isDeleted)}
                containerStyleClass={classes.addImageContainer}
                isUploading={uploadGovernmentDocumentFilesLoading?.isLoading}
                deleteId={deleteId}
                required={govtIdImageField.isRequired}
                skippable={govtIdImageField.showSkippableIndicator}
                error={errors.governmentDocumentFiles?.message}
                disabled={isArchivedProject}
                onCapture={onAddGovernmentDocumentFile}
                onDelete={onDeleteGovernmentDocumentFile}
                multiple={true}
                isPdfEnabled={false}
              />
              <SkippableFieldIndicator show={govtIdImageField.showSkippableIndicator || false} />
            </>
          )}
        </>
      )}

      <Divider className={classes.divider} />
      <SignatureSection workerId={workerId} languageCode={languageCode} />

      <NavigationButtons
        secondaryButtonProps={{
          isHidden: !canGoToPreviousStep || isArchivedProject,
          onClick: handlePreviousStep,
          testId: 'personal-info-back-button',
        }}
        primaryButtonProps={{
          label: getNextStepName() ? `${t('common.next')}: ${getNextStepName()}` : t('personalInformation.next'),
          isLoading,
          hasEndArrow: true,
          styleClass: classes.nextButton,
          testId: 'personal-info-next-button',
          onClick: handleSubmit,
        }}
      />

      {showPiiPinVerificationModal && (
        <PiiPinVerificationModals onClose={() => setShowPiiPinVerificationModal(false)} />
      )}

      {showCaptureModal && (
        <CapturePhoto
          isProceedLoading={isProfilePictureUploading}
          onClose={() => setShowCaptureModal(false)}
          onProceed={onUploadProfilePicture}
        />
      )}
      {showSkippableFieldsModal && (
        <SkippableFieldsModal
          isVisible={showSkippableFieldsModal}
          skippedFields={getSkippedFields(
            values,
            profileFieldConstraintsMap,
            hasReachedConsentForm,
            fieldDisplayNames,
            getDynamicFormFieldMap(values),
          )}
          isLoading={isLoading}
          onContinue={handleContinueWithSkippedFields}
          onFillFields={() => setShowSkippableFieldsModal(false)}
          onClose={() => setShowSkippableFieldsModal(false)}
        />
      )}
    </div>
  );

  // Only pass visible fields to WithWizard for sidebar display
  const visibleFields = [
    ...(profilePhotoField.isVisible
      ? [
          {
            name: profilePhotoField.name,
            id: profilePhotoField.id,
            isRequired: profilePhotoField.isRequired,
            isSkippable: profilePhotoField.isSkippable,
            skippableTill: profilePhotoField.skippableTill,
            isVisible: true as const,
          },
        ]
      : []),
    ...personalInformationFields,
    ...genderSectionFields,
    ...govtIdFields,
    govtIdImageField,
  ]
    .filter(field => field.isVisible)
    .map(field => ({
      name: field.name,
      id: field.id,
      isRequired: field.isRequired,
      isSkippable: field.isSkippable,
      skippableTill: field.skippableTill,
    }));

  return (
    <>
      <WithWizard disableNavigation={isLoading || isProfilePictureUploading} fields={visibleFields} content={content} />
    </>
  );
};

export default memo(PersonalInformation);
